import { useCallback, useEffect, useRef, useState } from 'react'
import { DefaultValues, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import captchaVerify from 'core/components/captchaVerify'
import { useResponsiveDevice } from 'core/utils/useResponsiveDevice'
import * as z from 'zod'
import { usePostV3LoginMutation, usePostV1SendValidCodeMutation } from 'core/query/yapi/hk-web/Hotcoin/query'
import { TLoginServiceResponse, TSendType } from 'core/service/hk-web/api'
import { useVerifyImage4LoginMutation } from 'core/query/hk-web/query'
import { HandledError } from 'core/types'
import { TAuthOptions } from 'core/components/Authenticate'
import { getSearchParams } from 'core/utils'
import { loginPhoneSchema, loginEmailSchema, emailOnlySchema, phoneOnlySchema } from './verify'
import RecordEvent from 'core/utils/RecordEvent/recordPCEvent'
// import { syncLoginTokenToMSite } from './tokenBridge'

// 外部通过这边的key来获取错误信息多语言
// 外部通过这边的key来获取错误信息多语言
export const emailErrorMessageKey = {
  msgEmailRequired: 'msgEmailRequired',
  msgEmailInvalid: 'msgEmailInvalid',
}
export const phoneErrorMessageKey = {
  msgPhoneNumberRequired: 'msgPhoneNumberRequired',
  msgPhoneShouldBeNumber: 'msgPhoneShouldBeNumber',
}
export const passwordErrorMessageKey = {
  msgPasswordRequired: 'msgPasswordRequired',
}

type TPhoneLoginSchema = z.infer<typeof loginPhoneSchema>
type TPhoneOnlySchema = z.infer<typeof phoneOnlySchema>
type TEmailLoginSchema = z.infer<typeof loginEmailSchema>
type TEmailOnlySchema = z.infer<typeof emailOnlySchema>
type TLoginSchema<T, K> = T extends 'phone'
  ? K extends true
    ? TPhoneLoginSchema
    : TPhoneOnlySchema
  : K extends true
    ? TEmailLoginSchema
    : TEmailOnlySchema

interface IController {
  toast?: (params: { type: 'success' | 'error' | 'warning' | 'info'; content: string }) => Promise<any>
  showRetryDialog?: (info: { key: string; message?: string }) => void
  showToast?: (key: string) => void
}

interface ILoginController<T, K> extends IController {
  defaultValues: TLoginSchema<T, K>
  type: T
  onLoginSuccess?: (data: TLoginServiceResponse) => void
}
type TLoginType = 'phone' | 'email'
type TVerifyType = TLoginType | 'google'

const verifyTypeMap = {
  phone: 1,
  email: 2,
  google: 3,
}

const verifyTypeMapResponseCode = {
  20718: 'email',
  20717: 'phone',
  20716: 'google',
}

export const useLogin = <T extends TLoginType, K extends boolean>({
  showRetryDialog,
  showToast,
  defaultValues,
  type,
  onLoginSuccess,
}: ILoginController<T, K>) => {
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false)
  const loginSchema =
    type === 'phone'
      ? passwordVisible
        ? loginPhoneSchema
        : phoneOnlySchema
      : passwordVisible
        ? loginEmailSchema
        : emailOnlySchema

  const form = useForm<TLoginSchema<T, K>>({
    defaultValues: defaultValues as DefaultValues<TLoginSchema<T, K>>,
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  })

  const {
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form

  const [captchaReady, setCaptchaReady] = useState(false)
  const responsiveDevice = useResponsiveDevice()
  const [verifyPending, setVerifyPending] = useState(false)
  const [verifyType, setVerifyType] = useState<TVerifyType>()
  const imageCode = useRef<string>()
  const [countDown, setCountDown] = useState(0)
  const { mutateAsync: login } = usePostV3LoginMutation({
    meta: {
      requestOptions: {
        signature: true,
        disableErrorCodeResponseHandler: true,
      },
    },
  })
  const { mutateAsync: verifyImage4Login } = useVerifyImage4LoginMutation()
  const { mutate: getVerifyCode } = usePostV1SendValidCodeMutation({
    meta: {
      requestOptions: {
        signature: true,
      },
    },
    onSuccess: () => {
      setCountDown(60)
    },
  })
  const getVerifyCodeTimes = useRef({
    email: 0,
    phone: 0,
  })

  const sendVerifyCode = (type?: string) => {
    if (!verifyType && !type) return
    const vType = verifyType || type
    getVerifyCode({
      address: getValues(vType),
      businessType: 'login_v3',
      sendType: TSendType[vType],
      seqNo: getVerifyCodeTimes.current[vType]++,
    })
  }

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined
    if (!!countDown) {
      timerId = setInterval(() => {
        if (countDown > 0) {
          setCountDown((prevCountdown) => prevCountdown - 1)
        } else {
          clearInterval(timerId)
        }
      }, 1000)
    }
    return () => {
      clearInterval(timerId)
    }
  }, [countDown])

  // 第一次提交登录表单
  const onSubmit = handleSubmit((data) => {
    RecordEvent.record('Login_LoginButton')
    setCaptchaReady(true)

    const loginData = {
      pwd: data.password,
      // @ts-ignore 必定存在
      address: data[type],
      validType: 0 as 0,
    }

    login(loginData)
      .then((loginRes) => {
        if (![20718, 20716, 20717].includes(loginRes.data.code)) {
          // 20718: 邮箱验证 20716: 谷歌验证 20717: 手机验证
          showRetryDialog?.({
            key: 'LoginFailed_VerifyCodeError',
            message: loginRes.data.msg,
          })
          setCaptchaReady(false)
          return
        }
        const sceneId = '18001001' // 待确认
        captchaVerify({ sceneId })
          .then((captchaResult) => {
            verifyImage4Login({
              ...loginData,
              captchaOutput: captchaResult.captcha_output,
              genTime: captchaResult.gen_time,
              lotNumber: captchaResult.lot_number,
              passToken: captchaResult.pass_token,
              sceneId,
            })
              .then((res) => {
                if (res.data.code !== 200) {
                  throw new Error(res.data.msg)
                }
                imageCode.current = captchaResult.pass_token
                setVerifyType(verifyTypeMapResponseCode[loginRes.data.code as 20718 | 20716 | 20717] as TVerifyType)
                if ([20717, 20718].includes(loginRes.data.code)) {
                  sendVerifyCode(verifyTypeMapResponseCode[loginRes.data.code as 20718 | 20716 | 20717])
                }
              })
              .catch((err) => {
                showRetryDialog?.({
                  key: 'VerifyImage4LoginError',
                  message: err.message,
                })
              })
              .finally(() => {
                setCaptchaReady(false)
              })
          })
          .catch((err: Error) => {
            if (err.message.includes('CaptchaCSSImageError')) {
              showToast?.('CaptchaCSSImageError')
            } else if (!err.message.includes('CaptchaClose')) {
              showRetryDialog?.({
                key: 'CaptchaInitError',
                message: err.message,
              })
            }
            setCaptchaReady(false)
          })
      })
      .catch((err: Error) => {
        if ((err as HandledError)?.handled) return
        showRetryDialog?.({
          key: 'CallLoginApiError',
        })
        setCaptchaReady(false)
      })
  })

  // 当输入邮箱或者手机验证码后的处理
  const handleVerifyInput = useCallback(
    (verifyCode: string) => {
      if (!verifyType) return
      setVerifyPending(true)
      login({
        // @ts-ignore todo:必定存在, 待优化ts
        address: getValues(type),
        // @ts-ignore todo:必定存在, 待优化ts
        pwd: getValues('password'),
        validType: verifyTypeMap[verifyType],
        validCode: verifyCode,
        imageCode: imageCode.current!,
      })
        .then((res) => {
          if (res.data.code !== 200) {
            throw new Error(res.data.msg)
          }
          // 保存登录账号信息
          window.localStorage.setItem(
            'LoginConfig',
            JSON.stringify({
              loginType: type,
              loginName: getValues(type),
            })
          )

          onLoginSuccess?.(res.data)
          setVerifyPending(false)

          // // 同步 token 到移动端
          // syncLoginTokenToMSite(res.data.data.token, JSON.stringify(res.data.data.userinfo))
          //   .then((result) => {
          //     onLoginSuccess?.({ ...res.data, disableRedirect: !!result?.disableRedirect })
          //     setVerifyPending(false)
          //   })
          //   .catch((error) => {
          //     // todo: 处理错误接入sentry
          //     console.error('Failed to sync token:', error)
          //   })
        })
        .catch((err: Error) => {
          setVerifyPending(false)
          showRetryDialog?.({
            key: 'LoginFailed',
            message: err.message,
          })
        })
    },
    [getValues, verifyType]
  )

  return {
    verify: {
      verifyPending,
      handleVerifyInput,
      verifyType,
      setVerifyType,
    },
    onSubmit,
    captchaReady,
    setCaptchaReady,
    form,
    sendVerifyCode,
    countDown,
    passwordVisible,
    setPasswordVisible,
  }
}

const handleAuthPath = (path: string, loginOptions?: TAuthOptions): string => {
  const currentLocation = window.location

  if (currentLocation.pathname.includes(`/${path}`)) {
    return ''
  }

  const queryParams = new URLSearchParams()

  // 处理重定向URL
  if (!loginOptions?.disableRedirect) {
    let redirectURL = getSearchParams('redirect') || currentLocation.href.replace(currentLocation.origin, '')

    if (!redirectURL.includes(`/${path}`) && !redirectURL.includes('/register') && !redirectURL.includes('/login')) {
      queryParams.set('redirect', redirectURL)
    }
  }

  // 处理邀请码
  const icCode = getSearchParams('icCode')
  const regAdType = getSearchParams('regAdType')
  const regAdId = getSearchParams('regAdId')
  if (icCode && !loginOptions?.disableICCode) {
    queryParams.set('icCode', icCode)
    if (regAdType) {
      queryParams.set('regAdType', regAdType)
    }
    if (regAdId) {
      queryParams.set('regAdId', regAdId)
    }
  }

  // kol专属活动页跳转登陆注册新增活动参数
  if (currentLocation.pathname.includes('/activity/kol/depositTransaction')) {
    const icCode = getSearchParams('inviteCode')
    const regAdId = getSearchParams('id')
    if (icCode && !loginOptions?.disableICCode) {
      queryParams.set('icCode', icCode)
    }
    if (regAdId) {
      queryParams.set('regAdType', '4')
      queryParams.set('regAdId', regAdId)
    }
  }

  const queryString = queryParams.toString()
  return queryString ? `/${path}?${queryString}` : `/${path}`
}
/**
 * 获取登录路径
 * @param loginOptions - 登录配置选项
 * @returns 返回登录页面的URL路径
 */
export const getLoginPath = (loginOptions?: TAuthOptions): string => {
  return handleAuthPath('login', loginOptions)
}

/**
 * 获取登录路径
 * @param loginOptions - 登录配置选项
 * @returns 返回登录页面的URL路径
 */
export const getRegisterPath = (loginOptions?: TAuthOptions): string => {
  return handleAuthPath('register', loginOptions)
}
